import React, { useMemo, useState } from 'react';
import { Product, ProductVariant } from '../types';
import { usePos } from '../context/PosContext';
import { printReportAuto } from '../services/receipt';
import './Entry.css';

// A goods-received line is keyed by product *and* variant: receiving 6 Large
// and 4 Small of the same shirt is two lines, and each one moves that variant's
// own stock.
interface Line {
  productId: string;
  variantId?: string;
  variantName?: string;
  name: string;
  qty: number;
  cost: number;
}

interface Pending {
  productId: string;
  variantId?: string;
  variantName?: string;
  name: string;
  qty: string;
  cost: string;
  price: string;
}

const lineKey = (productId: string, variantId?: string) => `${productId}::${variantId ?? ''}`;

// Matches goods_received_layout_content.xml.
const GoodsReceived: React.FC = () => {
  const { products, suppliers, receiveGoods, movements, config } = usePos();
  const [lines, setLines] = useState<Line[]>([]);
  const [search, setSearch] = useState('');
  const [pending, setPending] = useState<Pending | null>(null);
  const [variantPick, setVariantPick] = useState<Product | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [supplierId, setSupplierId] = useState('');

  const lastReceived = useMemo(() => {
    const recv = movements.filter((m) => m.reason === 'goods_received');
    return recv.length ? recv[recv.length - 1].quantity : 0;
  }, [movements]);

  // A scan resolves against variant barcodes and SKUs first — that is the whole
  // point of giving each attribute combination its own label — then falls back
  // to the product's own barcode / id / name.
  const resolve = (term: string): { product: Product; variant?: ProductVariant } | null => {
    const t = term.trim();
    const lower = t.toLowerCase();
    for (const p of products) {
      const variant = p.variants?.find(
        (v) => v.barcode === t || (v.sku && v.sku.toLowerCase() === lower)
      );
      if (variant) return { product: p, variant };
    }
    const p = products.find(
      (x) => x.barcode === t || String(x.id) === t || x.name.toLowerCase() === lower
    );
    return p ? { product: p } : null;
  };

  const startLine = (product: Product, variant?: ProductVariant) =>
    setPending({
      productId: product.id,
      variantId: variant?.id,
      variantName: variant?.name,
      name: variant ? `${product.name} · ${variant.name}` : product.name,
      qty: '1',
      cost: String(variant?.costPrice ?? product.costPrice ?? 0),
      price: String(variant?.price ?? product.price),
    });

  // Picking a product that has variants asks which one before the qty/cost row.
  const pickProduct = (product: Product) => {
    if (product.variants?.length) {
      setVariantPick(product);
      return;
    }
    startLine(product);
  };

  const onSearchKey = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key !== 'Enter' || !search.trim()) return;
    const hit = resolve(search.trim());
    if (hit) {
      if (hit.variant) startLine(hit.product, hit.variant);
      else pickProduct(hit.product);
      setSearch('');
    }
  };

  const pendingTotal = pending ? (parseFloat(pending.qty) || 0) * (parseFloat(pending.cost) || 0) : 0;

  const commit = () => {
    if (!pending) return;
    const qty = parseFloat(pending.qty) || 0;
    const cost = parseFloat(pending.cost) || 0;
    if (qty <= 0) return;
    const key = lineKey(pending.productId, pending.variantId);
    setLines((prev) => {
      const f = prev.find((l) => lineKey(l.productId, l.variantId) === key);
      if (f) {
        return prev.map((l) =>
          lineKey(l.productId, l.variantId) === key ? { ...l, qty: l.qty + qty, cost } : l
        );
      }
      return [
        ...prev,
        {
          productId: pending.productId,
          variantId: pending.variantId,
          variantName: pending.variantName,
          name: pending.name,
          qty,
          cost,
        },
      ];
    });
    setPending(null);
  };

  const removeLine = (key: string) =>
    setLines((prev) => prev.filter((l) => lineKey(l.productId, l.variantId) !== key));
  const total = lines.reduce((s, l) => s + l.qty * l.cost, 0);

  const addStock = () => {
    if (!lines.length) return;
    receiveGoods(
      lines.map((l) => ({
        productId: l.productId,
        variantId: l.variantId,
        variantName: l.variantName,
        quantity: l.qty,
      })),
      supplierId || undefined,
      'GR'
    );
    const received = lines.reduce((n, l) => n + l.qty, 0);
    setLines([]);
    // An inline note rather than alert(): a modal here blocks the till (and any
    // scanner input) until someone clicks it away.
    setNotice(`Stock added — ${received} unit(s) across ${lines.length} line(s).`);
    window.setTimeout(() => setNotice(null), 4000);
  };

  // Print the current goods-received draft as a thermal slip (ESC/POS),
  // falling back to the browser print dialog.
  const printSlip = () => {
    if (!lines.length) return;
    const rows: (string | number)[][] = lines.map((l) => [l.name, l.qty, l.cost.toFixed(2), (l.qty * l.cost).toFixed(2)]);
    rows.push(['', '', 'NET', total.toFixed(2)]);
    printReportAuto('Goods Received', config.storeName, ['Item', 'Qty', 'Cost', 'Total'], rows);
  };

  const productTiles = products.filter((p) => !search || p.name.toLowerCase().includes(search.toLowerCase()));

  return (
    <div className="es-page">
      <div className="es-head">
        <span>Goods Received</span>
        <span className="es-sub">{config.storeName}</span>
      </div>

      <div className="es-toolbar">
        <div className="es-search" style={{ flex: 1 }}>
          <input placeholder="Search by Code/Barcode/Name" value={search} onChange={(e) => setSearch(e.target.value)} onKeyDown={onSearchKey} />
          <div className="es-search-btn">🔍</div>
        </div>
        <select value={supplierId} onChange={(e) => setSupplierId(e.target.value)} style={{ minWidth: 180, padding: 10 }}>
          <option value="">Supplier…</option>
          {suppliers.map((s) => (
            <option key={s.id} value={s.id}>
              {s.name}
            </option>
          ))}
        </select>
      </div>

      {/* entry row: Qty | Cost Price | Price | Total */}
      <div className="es-entry">
        <input className="es-ebox" placeholder="Qty" value={pending?.qty ?? ''} onChange={(e) => pending && setPending({ ...pending, qty: e.target.value })} onKeyDown={(e) => e.key === 'Enter' && commit()} />
        <input className="es-ebox" placeholder="Cost Price" value={pending?.cost ?? ''} onChange={(e) => pending && setPending({ ...pending, cost: e.target.value })} onKeyDown={(e) => e.key === 'Enter' && commit()} />
        <input className="es-ebox" placeholder="Price" value={pending?.price ?? ''} onChange={(e) => pending && setPending({ ...pending, price: e.target.value })} />
        <div className="es-ebox total" onClick={commit} title="Add to list">
          {pendingTotal.toFixed(2)}
        </div>
      </div>

      <div className="es-items">
        {lines.length === 0 ? (
          <div className="es-empty">
            {pending ? `Adding ${pending.name} — set Qty/Cost, press Enter` : 'Search a product to receive stock'}
            {!pending && (
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginTop: 12, justifyContent: 'center' }}>
                {productTiles.slice(0, 12).map((p: Product) => (
                  <button
                    key={p.id}
                    className="es-btn-grey"
                    style={{ padding: '6px 10px' }}
                    onClick={() => pickProduct(p)}
                    title={p.variants?.length ? `${p.variants.length} variants` : undefined}
                  >
                    {p.name}
                    {p.variants?.length ? ` (${p.variants.length})` : ''}
                  </button>
                ))}
              </div>
            )}
          </div>
        ) : (
          lines.map((l) => (
            <div className="es-item" key={lineKey(l.productId, l.variantId)}>
              <span className="es-del" onClick={() => removeLine(lineKey(l.productId, l.variantId))}>
                ✕
              </span>
              <span className="es-item-name">{l.name}</span>
              <span className="es-orange">{l.qty}</span>
              <span className="es-x">X</span>
              <span className="es-orange">{l.cost.toFixed(2)}</span>
              <span className="es-x">=</span>
              <span className="es-item-total">{(l.qty * l.cost).toFixed(2)}</span>
            </div>
          ))
        )}
      </div>

      <div className="es-bottom">
        <div className="es-lastbill">Last Goods Received : {lastReceived.toFixed(2)}</div>
        <div className="es-netbar">NET AMT : {total.toFixed(2)}</div>
      </div>

      {variantPick && (
        <div className="es-modal-back" onClick={() => setVariantPick(null)}>
          <div className="es-modal" onClick={(e) => e.stopPropagation()}>
            <div className="es-modal-head">{variantPick.name} — which variant?</div>
            {variantPick.variants?.map((v) => (
              <button
                key={v.id}
                className="es-modal-row"
                onClick={() => {
                  startLine(variantPick, v);
                  setVariantPick(null);
                }}
              >
                <span>{v.name}</span>
                <span className="es-modal-meta">
                  stk {v.stock ?? 0}
                  {v.barcode ? ` · ${v.barcode}` : ''}
                </span>
              </button>
            ))}
            <button className="es-btn-grey" onClick={() => setVariantPick(null)}>
              Cancel
            </button>
          </div>
        </div>
      )}

      {notice && <div className="es-notice">{notice}</div>}

      <div className="es-actions">
        <button className="es-btn-orange" onClick={addStock} disabled={!lines.length}>
          ADD STOCK
        </button>
        <button className="es-btn-grey" onClick={printSlip} disabled={!lines.length}>
          + STOCK PRINT
        </button>
      </div>
    </div>
  );
};

export default GoodsReceived;
